import { Component, DestroyRef, inject, OnDestroy, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';

import { BAAS_CUSTOM_EVENT, BaasAppState, BaasToepassingService } from '@lvflanders/bar';
import { BarCoreService } from '@lvflanders/bar-ng';
import { BarI18nPipe } from '@lvflanders/bar-ng-ui';

import { StartschermV2FormatteerInternalService } from '../../services/v2/startscherm-v2-formatteer-internal.service';
import { StartschermV2StateService } from '../../services/v2/startscherm-v2-state.service';
import { StartschermVoorwieDialog } from './startscherm-voorwie-dialog/startscherm-voorwie-dialog';

@Component({
    selector: 'startscherm-voorwie-widget',
    templateUrl: './startscherm-voorwie-widget.html',
    styleUrls: ['./startscherm-voorwie-widget.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [MatTooltip, BarI18nPipe]
})
export class StartschermVoorwieWidget implements OnInit, OnDestroy {
    readonly #appState = inject(BaasAppState);
    readonly #core = inject(BarCoreService);
    readonly #toepassing = inject(BaasToepassingService);
    readonly #state = inject(StartschermV2StateService);
    readonly #dialog = inject(MatDialog);
    readonly #formatteerService = inject(StartschermV2FormatteerInternalService);
    readonly #destroyRef = inject(DestroyRef);

    protected readonly isVisible = signal(false);
    protected readonly voorwieChosen = signal(false);
    protected readonly client = signal<string | undefined>(undefined);
    protected readonly klantnummer = signal<string | undefined>(undefined);
    protected readonly voorwieTooltip = signal<string | undefined>(undefined);

    readonly #basiscontextChangedEventHandler = () => this.#init();

    ngOnInit(): void {
        this.#core.refreshed.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#init());
        // Listen for the custom event on a JavaScript object or DOM element
        window.addEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChangedEventHandler);
        this.#init();
    }

    ngOnDestroy(): void {
        window.removeEventListener(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, this.#basiscontextChangedEventHandler);
    }

    // invoked when user opens the popup
    protected search(): void {
        const dialogRef = this.#dialog.open(StartschermVoorwieDialog, {
            minWidth: '70vw',
            data: { client: this.client(), klantnummer: this.klantnummer() }
        });

        dialogRef.afterClosed().subscribe(async (res) => {
            if (this.#appState.toepassing.code == 'STA2BUI') {
                window.location.reload();
            }
            if (res && res.reset) await this.reset();
        });
    }

    protected async reset(): Promise<void> {
        // reset hoedanigheid & voorwie and go to home
        await this.#toepassing.resetHoedanigheid();
        await this.#toepassing.resetVoorwie();
        this.#core.navigateHome(false);
    }

    async #init() {
        let beheersdienst = false;
        if (this.#appState.basiscontext?.hoedanigheid && this.#appState.basiscontext?.hoedanigheidRolcode) {
            // check whether current hoedanigheid is a beheersdienst
            beheersdienst = this.#appState?.userInfo?.hoedanigheidInfoSet?.some(
                (hi) =>
                    hi.isBeheersdienst &&
                    hi.rolcode === this.#appState.basiscontext.hoedanigheidRolcode &&
                    hi.value === this.#appState.basiscontext.hoedanigheid
            );
        }
        this.isVisible.set(this.#state.isExtern && this.#state.aantalVoorwies > 1 && !beheersdienst);
        this.voorwieChosen.set(this.#appState.basiscontext && !!this.#appState.basiscontext.voorwie);
        await this.#state.refreshVoorwiePicker();
        this.client.set(this.#state.klantnaam);
        this.klantnummer.set(this.#formatteerService.formatteerKlantnummer(this.#state.klantnummer));
        const voorwieTooltip = `${this.#state.klantnaam ? this.#state.klantnaam : ''} (${this.#state.klantnummer ? this.#formatteerService.formatteerKlantnummer(this.#state.klantnummer) : ''}) \n ${
            this.#state.klantadres ? this.#state.klantadres : ''
        }\n${this.#state.klantnummer != this.#state.identificatienummer ? (this.#state.identificatienummer ? this.#state.identificatienummer : '') + (this.#state.omschrijvingIdentificatienummer ? ' - ' + this.#state.omschrijvingIdentificatienummer : '') : ''}`;
        this.voorwieTooltip.set(voorwieTooltip);
    }
}
